import { Injectable } from "@angular/core";
import { CanMatch, Route } from "@angular/router";
import { Observable, of } from "rxjs";
import { catchError, finalize, map, shareReplay, switchMap, take } from "rxjs/operators";
import { CmsContentstackRestClientService } from "@usana/ux/cms";
import { UserService } from "../modules/common/user.service";
import { LocaleUtil } from "../modules/util/locale-util";

export type CmsStatus = "cms" | "noncms" | "error";

/**
 * Route guard that controls whether a route matches based on CMS market status.
 *
 * Probes Contentstack to check whether a nav_header entry has been published
 * for the exact requested locale (not a fallback). Because the Contentstack
 * client always sends include_fallback=true, the entry's top-level `locale`
 * field always reflects the master locale (en-us) and cannot be used as a
 * discriminator. Instead, we check `publish_details.locale`, which reflects
 * the locale the entry was actually published for — a mismatch means it is a
 * fallback and the locale is not a CMS market.
 *
 * Successful results (cms or noncms) are cached for the lifetime of the session.
 * Errors are not cached — a Contentstack outage renders a blank nav and retries
 * automatically on the next navigation.
 *
 * Concurrent calls for the same locale share a single in-flight request.
 */
@Injectable({
    providedIn: "root",
})
export class CmsTypeGuard implements CanMatch {
    private static readonly NAV_HEADER_CONTENT_TYPE = "nav_header";

    private readonly cache = new Map<string, CmsStatus>();
    private readonly inFlight = new Map<string, Observable<CmsStatus>>();

    constructor(
        private userService: UserService,
        private cmsClient: CmsContentstackRestClientService,
    ) {}

    cmsStatus(): Observable<CmsStatus> {
        return this.userService.getShopUser().pipe(
            take(1),
            switchMap((user) => this.checkLocale(
                LocaleUtil.getUrlLocale(user.iso2Locale)
            )),
        );
    }

    canMatch(route: Route): Observable<boolean> {
        return this.cmsStatus().pipe(
            map((status) => {
                const cmsType = route.data?.["cmsType"];
                if (cmsType === "cms") {
                    return status === "cms";
                }
                if (cmsType === "noncms") {
                    return status !== "cms";
                }
                return true;
            }),
        );
    }

    private checkLocale(locale: string): Observable<CmsStatus> {
        const cached = this.cache.get(locale);
        if (cached) {
            return of(cached);
        }

        const existing = this.inFlight.get(locale);
        if (existing) {
            return existing;
        }

        const localeLower = locale.toLowerCase();

        const request$ = this.cmsClient.getEntries<Record<string, unknown>>({
            contentTypeUid: CmsTypeGuard.NAV_HEADER_CONTENT_TYPE,
            locale: localeLower,
            limit: 1,
            only: ["title", "locale", "publish_details"],
        }).pipe(
            take(1),
            map((entries) => {
                const entry = entries?.[0];
                // include_fallback=true means a returned entry may belong to the master
                // locale (en-us) rather than the requested one. entry["locale"] always
                // reflects the master/base locale, not the requested one. Instead, use
                // publish_details.locale which reflects the locale the entry was actually
                // published for — a mismatch means it's a fallback and not a CMS market.
                const publishLocale = (entry?.["publish_details"] as Record<string, string> | undefined)?.["locale"];
                const isCms = !!entry && publishLocale?.toLowerCase() === localeLower;
                const status: CmsStatus = isCms ? "cms" : "noncms";
                this.cache.set(locale, status);
                return status;
            }),
            catchError(() => {
                // Contentstack unreachable — do not cache so the next navigation retries.
                return of("error" as CmsStatus);
            }),
            // Remove the in-flight entry whether the request succeeded or errored.
            finalize(() => this.inFlight.delete(locale)),
            // Multicast so concurrent subscribers share one HTTP request instead of each
            // triggering their own, and replay the result to any late subscribers.
            shareReplay(1),
        );

        // Register before returning so concurrent calls find it immediately.
        this.inFlight.set(locale, request$);
        return request$;
    }
}
